import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import Button from '../common/Button';
import Modal from '../common/Modal';
import SearchableSelect from '../common/SearchableSelect';

// Mutăm FormField în afara componentei pentru a evita re-crearea la fiecare render
const FormField = ({ label, field, type = 'text', required = false, children, formData, errors, onInputChange, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children || (
      <input
        type={type}
        value={formData[field]}
        onChange={(e) => onInputChange(field, e.target.value)}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          errors[field] ? 'border-red-300' : 'border-gray-300'
        }`}
        {...props}
      />
    )}
    {errors[field] && (
      <p className="mt-1 text-sm text-red-600">{errors[field]}</p>
    )}
  </div>
);

const ContractForm = ({
  contract = null,
  clients = [],
  services = [],
  templates = [],
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  preselectedClientId = null // New prop for pre-selecting client
}) => {
  const [formData, setFormData] = useState({
    clientId: '',
    templateId: '',
    number: '',
    startDate: new Date().toISOString().split('T')[0],
    dueDate: `${new Date().getFullYear()}-12-31`,
    customContent: '',
    services: []
  });

  const [errors, setErrors] = useState({});

  // Generează un număr de contract automat
  const generateContractNumber = () => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `CT${year}${month}${random}`;
  };

  useEffect(() => {
    if (contract) {
      setFormData({
        clientId: contract.clientId?.toString() || '',
        templateId: contract.templateId?.toString() || '',
        number: contract.number || '',
        startDate: contract.startDate?.split('T')[0] || new Date().toISOString().split('T')[0],
        dueDate: contract.dueDate?.split('T')[0] || `${new Date().getFullYear()}-12-31`,
        customContent: contract.customContent || '',
        services: contract.services?.map(s => ({
          serviceId: s.serviceId.toString(),
          quantity: s.quantity,
          customPrice: s.customPrice || null
        })) || []
      });
    } else {
      setFormData({
        clientId: preselectedClientId?.toString() || '',
        templateId: '',
        number: generateContractNumber(),
        startDate: new Date().toISOString().split('T')[0],
        dueDate: `${new Date().getFullYear()}-12-31`,
        customContent: '',
        services: []
      });
    }
    setErrors({});
  }, [contract, isOpen, preselectedClientId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.clientId) {
      newErrors.clientId = 'Selectați un client';
    }

    if (!formData.number.trim()) {
      newErrors.number = 'Numărul contractului este obligatoriu';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Data de început este obligatorie';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Data de expirare este obligatorie';
    }

    if (formData.startDate && formData.dueDate && new Date(formData.startDate) >= new Date(formData.dueDate)) {
      newErrors.dueDate = 'Data de expirare trebuie să fie după data de început';
    }

    if (formData.services.length === 0) {
      newErrors.services = 'Adăugați cel puțin un serviciu';
    }

    formData.services.forEach((service, index) => {
      if (!service.serviceId) {
        newErrors[`service_${index}`] = 'Selectați un serviciu';
      }
      if (service.quantity <= 0) {
        newErrors[`quantity_${index}`] = 'Cantitatea trebuie să fie pozitivă';
      }
      if (service.customPrice !== null && service.customPrice < 0) {
        newErrors[`price_${index}`] = 'Prețul trebuie să fie pozitiv';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        ...formData,
        clientId: parseInt(formData.clientId),
        templateId: formData.templateId ? parseInt(formData.templateId) : null,
        services: formData.services.map(s => ({
          serviceId: parseInt(s.serviceId),
          quantity: parseInt(s.quantity),
          customPrice: s.customPrice ? parseFloat(s.customPrice) : null
        }))
      };
      onSubmit(submitData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const addService = () => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, { serviceId: '', quantity: 1, customPrice: null }]
    }));
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const updateService = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map((service, i) => 
        i === index ? { ...service, [field]: value } : service
      )
    }));
  };

  const getServicePrice = (serviceId) => {
    const service = services.find(s => s.id.toString() === serviceId);
    return service ? `${service.price} ${service.currency}` : '';
  };

  // Check if client is preselected (read-only mode)
  const isClientReadOnly = !!preselectedClientId;
  const selectedClient = clients.find(c => c.id.toString() === formData.clientId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={contract ? 'Editează Contract' : 'Contract Nou'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Client și Template */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client <span className="text-red-500">*</span>
            </label>
            {isClientReadOnly ? (
              // Read-only client display
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">
                    {selectedClient?.name}
                  </span>
                  {selectedClient?.fiscalCode && (
                    <span className="text-xs text-gray-500 mt-0.5">
                      CIF: {selectedClient.fiscalCode}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              // Editable client select
              <SearchableSelect
                options={clients}
                value={formData.clientId}
                onChange={(value) => handleInputChange('clientId', value)}
                placeholder="Selectați clientul"
                searchPlaceholder="Căutare client după nume..."
                error={!!errors.clientId}
                getOptionLabel={(client) => client.name}
                getOptionValue={(client) => client.id}
                renderOption={(client, isSelected) => (
                  <div className="flex flex-col">
                    <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                      {client.name}
                    </span>
                    {client.fiscalCode && (
                      <span className="text-xs text-gray-500 mt-0.5">
                        CIF: {client.fiscalCode}
                      </span>
                    )}
                  </div>
                )}
                emptyMessage="Nu s-au găsit clienți"
              />
            )}
            {errors.clientId && (
              <p className="mt-1 text-sm text-red-600">{errors.clientId}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template
            </label>
            <SearchableSelect
              options={templates}
              value={formData.templateId}
              onChange={(value) => handleInputChange('templateId', value)}
              placeholder="Fără template"
              searchPlaceholder="Căutare template..."
              error={!!errors.templateId}
              getOptionLabel={(template) => template.name}
              getOptionValue={(template) => template.id}
              emptyMessage="Nu s-au găsit template-uri"
            />
            {errors.templateId && (
              <p className="mt-1 text-sm text-red-600">{errors.templateId}</p>
            )}
          </div>
        </div>

        {/* Număr Contract */}
        <FormField
          label="Număr Contract"
          field="number"
          required
          placeholder="ex: CT202501001"
          formData={formData}
          errors={errors}
          onInputChange={handleInputChange}
        />

        {/* Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Data Început"
            field="startDate"
            type="date"
            required
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
          />

          <FormField
            label="Data Expirare"
            field="dueDate"
            type="date"
            required
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
          />
        </div>

        {/* Servicii */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Servicii <span className="text-red-500">*</span>
            </label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              icon={Plus}
              onClick={addService}
            >
              Adaugă Serviciu
            </Button>
          </div>

          {errors.services && (
            <p className="mb-3 text-sm text-red-600">{errors.services}</p>
          )}

          <div className="space-y-3">
            {formData.services.map((service, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-3">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Serviciu</label>
                    <SearchableSelect
                      options={services}
                      value={service.serviceId}
                      onChange={(value) => updateService(index, 'serviceId', value)}
                      placeholder="Selectați serviciul"
                      searchPlaceholder="Căutare serviciu..."
                      error={!!errors[`service_${index}`]}
                      getOptionLabel={(s) => s.name}
                      getOptionValue={(s) => s.id}
                      renderOption={(s, isSelected) => (
                        <div className="flex flex-col">
                          <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                            {s.name}
                          </span>
                          <span className="text-xs text-gray-500 mt-0.5">
                            {s.price} {s.currency} / {s.unit}
                          </span>
                        </div>
                      )}
                      emptyMessage="Nu s-au găsit servicii"
                    />
                    {errors[`service_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`service_${index}`]}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Cantitate</label>
                    <input
                      type="number"
                      min="1"
                      value={service.quantity}
                      onChange={(e) => updateService(index, 'quantity', parseInt(e.target.value) || 1)}
                      className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors[`quantity_${index}`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors[`quantity_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`quantity_${index}`]}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Preț Custom</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={service.customPrice || ''}
                      onChange={(e) => updateService(index, 'customPrice', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder={getServicePrice(service.serviceId)}
                      className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors[`price_${index}`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors[`price_${index}`] && <p className="mt-1 text-xs text-red-600">{errors[`price_${index}`]}</p>}
                  </div>

                  <div>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      icon={Trash2}
                      onClick={() => removeService(index)}
                      className="w-full"
                    >
                      Șterge
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Conținut Custom */}
        <FormField
          label="Conținut Personalizat"
          field="customContent"
          formData={formData}
          errors={errors}
          onInputChange={handleInputChange}
        >
          <textarea
            value={formData.customContent}
            onChange={(e) => handleInputChange('customContent', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Clauze sau conținut specific pentru acest contract..."
          />
        </FormField>

        <div className="flex justify-end space-x-3 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Anulează
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={loading}
          >
            {contract ? 'Actualizează' : 'Creează'} Contract
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ContractForm;